import TrophyIcon from '@mui/icons-material/EmojiEventsOutlined';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { useStore } from '@stores';
import { globalTheme as theme } from '@styles';
import { PrequalDetails } from '@types';

/** USD formatting matches ProgramDetails — currency stays en-US regardless of UI language. */
const formatUsd = (amount: number) =>
  amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const DAGGER = '†';

/**
 * Program + potential savings callout shown on the results-page win-back.
 *
 * Mirrors the card the user has selected on the results screen, falling back to
 * the recommended program before a selection is registered. The "Good Fit" badge
 * only appears when that program *is* the recommended one.
 *
 * Returns null when there is no program or no savings estimate for it — the
 * win-back then shows its copy without a card.
 */
export const WinBackSavingsCard = observer(() => {
  const { t } = useTranslation('module_result_options', {
    keyPrefix: 'content.programs',
  });
  const {
    commonStore: { selectedResultsProgram },
    decisionEngineStore: { recommendedProgram, getSavingsEstimateResult },
  } = useStore();

  const program = selectedResultsProgram ?? recommendedProgram;

  if (!program) return null;

  const estimatedSavings = getSavingsEstimateResult(program.name)
    ?.estimatedSavings as number | undefined;

  if (typeof estimatedSavings !== 'number') return null;

  const isRecommended = program.rule === recommendedProgram?.rule;

  const { title } = t(program.rule, {
    returnObjects: true,
  }) as PrequalDetails;

  return (
    <Box
      data-cy="winback-savings-card"
      sx={{
        width: '100%',
        maxWidth: { xs: '333px', md: '414px' },
        borderRadius: '16px',
        border: `1px solid ${theme.palette.custom.winBackCardBorder}`,
        backgroundColor: 'common.white',
        overflow: 'hidden',
      }}
    >
      {/* Header: program name + Good Fit badge, centred as a pair.
          Top-weighted per the design (16px above, flush to the body below). */}
      <Box
        sx={{
          pt: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
        }}
      >
        <Typography
          data-cy="winback-savings-program"
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '19.44px',
            color: theme.palette.brandDarkBlue.main,
          }}
        >
          {title}
        </Typography>
        {isRecommended && (
          <Box
            data-cy="winback-good-fit-badge"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              height: '22px',
              px: '6px',
              borderRadius: '8px',
              backgroundColor: theme.palette.custom.winBackGoodFitBadge,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                lineHeight: '15px',
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                color: 'common.white',
                whiteSpace: 'nowrap',
              }}
            >
              {t('goodFit')}
            </Typography>
            <TrophyIcon
              sx={{ width: '18px', height: '18px', color: 'common.white' }}
            />
          </Box>
        )}
      </Box>

      {/* Body: potential savings + estimate disclaimer */}
      <Box sx={{ px: '12px', pb: '10px', textAlign: 'center' }}>
        <Typography
          sx={{
            pt: { xs: '16px', md: '20px' },
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '15px',
            color: theme.palette.custom.winBackSubtleText,
          }}
        >
          {t('savings')}
        </Typography>
        <Typography
          data-cy="winback-savings-amount"
          sx={{
            // Design puts the amount flush under the label — the 52px line box
            // supplies the breathing room, so no extra margin here.
            mt: 0,
            fontSize: '40px',
            fontWeight: 600,
            lineHeight: '52px',
            letterSpacing: '-0.15px',
            color: theme.palette.brandBlue.main,
          }}
        >
          {formatUsd(estimatedSavings)}
          <Typography
            component="sup"
            sx={{
              fontSize: '9px',
              fontWeight: 700,
              lineHeight: '15px',
              color: theme.palette.custom.winBackDaggerText,
              // Sits at the top of the amount's line box, as in the design,
              // rather than the browser's default (lower) superscript offset.
              verticalAlign: 'top',
            }}
          >
            {DAGGER}
          </Typography>
        </Typography>
        {/* Disclaimer: the dagger hangs at the container's left edge (design has it
            at x=0 with the copy starting at x=6), not inline against the text. */}
        <Box sx={{ position: 'relative', mt: '10px' }}>
          <Typography
            component="span"
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              fontSize: '9px',
              fontWeight: 700,
              lineHeight: '13px',
              color: theme.palette.custom.winBackDaggerText,
            }}
          >
            {DAGGER}
          </Typography>
          <Typography
            sx={{
              pl: '6px',
              fontSize: '10px',
              fontWeight: 400,
              lineHeight: '13px',
              color: theme.palette.custom.winBackDisclaimerText,
            }}
          >
            {t('disclaimerBody')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});
